import { createContext, useCallback, useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { AuthUser, LoginInput } from '@boodschappenlijst/shared';
import { setSessionLostHandler } from '../../lib/apiClient';
import { authApi } from './auth.api';

export interface AuthContextValue {
  user: AuthUser | null;
  loading: boolean;
  login: (input: LoginInput) => Promise<void>;
  logout: () => Promise<void>;
}

// eslint-disable-next-line react-refresh/only-export-components
export const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);
  const queryClient = useQueryClient();

  useEffect(() => {
    authApi
      .me()
      .then((r) => setUser(r.user))
      .catch(() => setUser(null))
      .finally(() => setLoading(false));
  }, []);

  /** Alles wat aan de vorige sessie hing weggooien, zodat de volgende gebruiker
   *  achter hetzelfde scherm niet de lijst van zijn voorganger ziet staan. */
  const clearSession = useCallback(() => {
    setUser(null);
    queryClient.clear();
  }, [queryClient]);

  // Nu de sessie na een uur inactiviteit vervalt, gebeurt dit in gewoon gebruik:
  // een request komt terug met 401 en dan moet de app naar het inlogscherm, in
  // plaats van per pagina een losse foutmelding te tonen.
  useEffect(() => {
    setSessionLostHandler(clearSession);
    return () => setSessionLostHandler(null);
  }, [clearSession]);

  const login = useCallback(async (input: LoginInput) => {
    const { user } = await authApi.login(input);
    setUser(user);
  }, []);

  const logout = useCallback(async () => {
    await authApi.logout();
    clearSession();
  }, [clearSession]);

  return (
    <AuthContext.Provider value={{ user, loading, login, logout }}>{children}</AuthContext.Provider>
  );
}
